import type {
  AirQualityObservation,
  Alert,
  DataSource,
  IssForecast,
  LocalEvent,
  NewsItem,
  NetworkStatus,
  Place,
  PrecipitationNowcast,
  SkyOutlook,
  TimelineItem,
  TransportDeparture,
  TransportDisruption,
  TransportLine,
  TransportStop,
  WaterQuality,
  WeatherObservation,
} from '../domain/index.ts';
import type { FreshnessMeta } from '../freshness/index.ts';

/** Contrat HTTP entre le Worker et le front. Versionné par le préfixe /api/v1. */
export const API_PREFIX = '/api/v1';

export type SectionErrorCode = 'not_configured' | 'provider_error' | 'timeout' | 'schema_mismatch' | 'network_error';

/**
 * Un bloc de données indépendant (ADR-0004). Le statut de fraîcheur n'est JAMAIS transmis :
 * le client le calcule à partir de `meta`.
 */
export interface DataSection<T> {
  readonly data: T | null;
  readonly meta: FreshnessMeta;
  readonly sources: readonly DataSource[];
  /** true ⇒ données fictives (fournisseur « fixture »), à étiqueter « Démo ». */
  readonly demo: boolean;
  readonly error: { readonly code: SectionErrorCode; readonly message: string } | null;
}

interface Envelope {
  readonly serverTime: string;
  readonly city: string;
}

export interface NowResponse extends Envelope {
  readonly weather: DataSection<WeatherObservation>;
  readonly nowcast: DataSection<PrecipitationNowcast>;
  readonly airQuality: DataSection<AirQualityObservation>;
  readonly network: DataSection<{
    readonly status: NetworkStatus;
    readonly disruptions: readonly TransportDisruption[];
  }>;
  readonly alerts: DataSection<readonly Alert[]>;
}

export interface SkyResponse extends Envelope {
  readonly sky: DataSection<SkyOutlook>;
}

export interface StopsResponse extends Envelope {
  readonly stops: readonly TransportStop[];
  readonly defaultStopId: string;
  /** Lignes desservant la ville : couleurs et libellés officiels pour l'affichage. */
  readonly lines: readonly TransportLine[];
}

export interface DeparturesResponse extends Envelope {
  readonly stop: TransportStop;
  readonly departures: DataSection<readonly TransportDeparture[]>;
}

export interface TimelineResponse extends Envelope {
  readonly date: string;
  readonly items: DataSection<readonly TimelineItem[]>;
}

export interface EventsResponse extends Envelope {
  readonly events: DataSection<readonly LocalEvent[]>;
}

export interface NewsResponse extends Envelope {
  readonly news: DataSection<readonly NewsItem[]>;
}

export interface IssResponse extends Envelope {
  readonly iss: DataSection<IssForecast>;
}

export interface WaterResponse extends Envelope {
  readonly water: DataSection<WaterQuality>;
}

export interface PlacesResponse extends Envelope {
  readonly places: DataSection<readonly Place[]>;
}

export interface PlaceResponse extends Envelope {
  readonly place: Place;
}

export interface SourceHealth {
  readonly key: string;
  readonly provider: string;
  readonly lastSuccessAt: string | null;
  readonly lastFailureAt: string | null;
  readonly lastErrorCode: SectionErrorCode | null;
  readonly lastLatencyMs: number | null;
  readonly lastItemCount: number | null;
}

export interface HealthResponse {
  readonly serverTime: string;
  readonly dataMode: 'fixtures' | 'live';
  readonly sources: readonly SourceHealth[];
}

/** Chemins de l'API, partagés entre routeur et client. */
export const apiPaths = {
  now: (city: string) => `${API_PREFIX}/cities/${city}/now`,
  sky: (city: string) => `${API_PREFIX}/cities/${city}/sky`,
  stops: (city: string) => `${API_PREFIX}/cities/${city}/stops`,
  departures: (city: string, stopId: string) =>
    `${API_PREFIX}/cities/${city}/departures?stop=${encodeURIComponent(stopId)}`,
  timeline: (city: string, date: string) => `${API_PREFIX}/cities/${city}/timeline?date=${date}`,
  events: (city: string) => `${API_PREFIX}/cities/${city}/events`,
  news: (city: string) => `${API_PREFIX}/cities/${city}/news`,
  water: (city: string) => `${API_PREFIX}/cities/${city}/water`,
  iss: (city: string) => `${API_PREFIX}/cities/${city}/iss`,
  places: (city: string) => `${API_PREFIX}/cities/${city}/places`,
  shops: (city: string) => `${API_PREFIX}/cities/${city}/shops`,
  place: (city: string, slug: string) => `${API_PREFIX}/cities/${city}/places/${encodeURIComponent(slug)}`,
  mapLayer: (city: string, layer: string) => `${API_PREFIX}/cities/${city}/map/${layer}`,
  health: () => `${API_PREFIX}/health/sources`,
} as const;
